// The chat.profanity_words control: a count, a button, and the dialog behind
// it. Ports .codelane/artifacts/t-102/config-profanity-list.html L480.
//
// Everything the operator does happens in the dialog; this row only says how
// many words there are and whether they are the saved ones. The value is still
// the plain `string[]` the config has always carried — the modal hands one back
// on Done, and it leaves on the page's own Save.

import { useState } from "react";
import { Edit3, Plus } from "lucide-react";

import { Button } from "../../../components/Button/Button";
import { ProfanityWordsModal } from "./ProfanityWordsModal";
import { wordsChangeNote } from "./profanityWords";
import { isEqual } from "./utils";
import styles from "./ProfanityWordsField.module.css";

interface Props {
  value: string[];
  /** The saved list — what "modified" and the note below are measured against. */
  serverValue: string[];
  /** Names the file the dialog's Export writes. */
  projectName: string;
  onChange: (next: string[]) => void;
}

export function ProfanityWordsField({
  value,
  serverValue,
  projectName,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const modified = !isEqual(serverValue, value);
  const empty = value.length === 0;

  return (
    <>
      <div className={`${styles.summary} ${modified ? styles.modified : ""}`}>
        {empty ? (
          <span className={`${styles.count} ${styles.empty}`}>
            No custom words
          </span>
        ) : (
          <span className={styles.count}>
            <b>{value.length}</b> {value.length === 1 ? "word" : "words"}
          </span>
        )}
        <span className={styles.spacer} />
        <Button
          variant="secondary"
          size="sm"
          icon={empty ? <Plus size={14} /> : <Edit3 size={14} />}
          onClick={() => setOpen(true)}
        >
          {empty ? "Add words" : "Edit list"}
        </Button>
      </div>
      {modified && (
        <div className={styles.note}>{wordsChangeNote(serverValue, value)}</div>
      )}
      {/* Mounted per opening, so its draft starts from the current value and
          Cancel has nothing left to undo. */}
      {open && (
        <ProfanityWordsModal
          words={value}
          serverWords={serverValue}
          projectName={projectName}
          onDone={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
